import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listGoogleConnections, listGbpLocationsOverview, syncGoogleConnectionLocations,
  type GoogleConnectionDto, type GbpLocationOverview, type GbpSyncResult,
} from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { GBIBadge, GBIBtn, GBICard, GBILabel, T, sharedStyles } from '../components/Shared';

export function AdminGbpAccess({ embedded = false }: { embedded?: boolean }) {
  const [reloadKey, setReloadKey] = useState(0);
  const connState = useAsync(listGoogleConnections, [reloadKey]);
  const locState = useAsync(listGbpLocationsOverview, [reloadKey]);
  const [onlyNeedsAttention, setOnlyNeedsAttention] = useState(true);

  const reload = () => setReloadKey(k => k + 1);

  const connections = connState.status === 'success' ? connState.data : [];
  const locations = locState.status === 'success' ? locState.data : [];

  const sortedLocations = [...locations].sort((a, b) => {
    if (a.hasAccount !== b.hasAccount) return a.hasAccount ? 1 : -1;
    return (a.displayName ?? '').localeCompare(b.displayName ?? '');
  });
  const visibleLocations = onlyNeedsAttention ? sortedLocations.filter(l => !l.hasAccount) : sortedLocations;
  const needsAttentionCount = sortedLocations.filter(l => !l.hasAccount).length;

  return (
    // Own scroll container: global CSS locks body{overflow:hidden} on desktop,
    // so the page must scroll itself or content gets clipped.
    <div style={{ height: embedded ? 'auto' : '100vh', overflowY: embedded ? 'visible' : 'auto', background: '#f8f9fa', padding: embedded ? 0 : '40px 32px' }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        {!embedded && <>
          <Link to="/admin" style={{ fontSize: 12, color: T.fgSecondary, textDecoration: 'none' }}>← Back to clients</Link>
          <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 28, fontWeight: 700, color: T.fgPrimary, marginTop: 8, marginBottom: 4 }}>
            GBP Access Overview
          </div>
          <div style={{ fontSize: 13, color: T.fgSecondary, marginBottom: 24 }}>
            Every Google Business Profile location we have manager access to, whether it's linked to a client account, and its Client Intake status — so the team knows who to onboard next.
          </div>
        </>}

        {connState.status === 'error' && (
          <Banner color={T.magenta}>Failed to load connections: {connState.error.message}</Banner>
        )}
        {locState.status === 'error' && (
          <Banner color={T.magenta}>Failed to load locations: {locState.error.message}</Banner>
        )}

        {connState.status === 'loading' && (
          <div style={{ color: T.fgMuted, fontSize: 13, padding: 16 }}>Loading…</div>
        )}

        {connState.status === 'success' && connections.length === 0 && (
          <GBICard>
            <div style={{ color: T.fgMuted, fontSize: 13, marginBottom: 12 }}>
              No Google account is connected yet. Connect one first — the system will then discover which client GBPs it can manage.
            </div>
            <Link to="/admin/gbp?tab=accounts" style={{ textDecoration: 'none' }}>
              <GBIBtn variant="primary">Connect a Google account →</GBIBtn>
            </Link>
          </GBICard>
        )}

        {connState.status === 'success' && connections.length > 0 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
              {connections.map(c => (
                <ConnectionSyncRow key={c.id} connection={c} onSynced={reload} />
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <GBILabel style={{ marginBottom: 0 }}>Discovered locations</GBILabel>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: T.fgSecondary }}>
                <span>{needsAttentionCount} of {sortedLocations.length} locations need an account</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={onlyNeedsAttention}
                    onChange={e => setOnlyNeedsAttention(e.target.checked)}
                  />
                  Show only locations needing attention
                </label>
              </div>
            </div>

            {locState.status === 'loading' && (
              <div style={{ color: T.fgMuted, fontSize: 13, padding: 16 }}>Loading…</div>
            )}

            {locState.status === 'success' && sortedLocations.length === 0 && (
              <GBICard>
                <div style={{ color: T.fgMuted, fontSize: 13 }}>
                  No locations discovered yet — refresh GBP locations above once your Google account has GBP manager access.
                </div>
              </GBICard>
            )}

            {locState.status === 'success' && sortedLocations.length > 0 && visibleLocations.length === 0 && (
              <GBICard>
                <div style={{ color: T.fgMuted, fontSize: 13 }}>
                  All discovered locations already have an account. Uncheck the toggle above to see everything.
                </div>
              </GBICard>
            )}

            {visibleLocations.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {visibleLocations.map(loc => (
                  <LocationRow key={loc.id} location={loc} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ConnectionSyncRow({ connection, onSynced }: { connection: GoogleConnectionDto; onSynced: () => void }) {
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState<GbpSyncResult | null>(null);

  const ageMs = connection.lastUsedAt ? Date.now() - connection.lastUsedAt : null;
  const lastSyncedLabel = ageMs == null
    ? 'never'
    : ageMs < 60_000 ? 'just now'
    : ageMs < 3_600_000 ? `${Math.floor(ageMs / 60_000)}m ago`
    : ageMs < 86_400_000 ? `${Math.floor(ageMs / 3_600_000)}h ago`
    : `${Math.floor(ageMs / 86_400_000)}d ago`;

  const handleSync = async () => {
    setSyncing(true);
    setResult(null);
    try {
      const res = await syncGoogleConnectionLocations(connection.id);
      setResult(res);
    } catch (err) {
      setResult({ locationsFound: 0, locationsUpserted: 0, error: err instanceof Error ? err.message : String(err) });
    } finally {
      setSyncing(false);
      onSynced();
    }
  };

  return (
    <GBICard style={{ padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: T.fgPrimary }}>
          <strong>{connection.googleEmail}</strong> · {connection.locationCount} location{connection.locationCount === 1 ? '' : 's'} discovered · last refreshed {lastSyncedLabel}
        </div>
        <GBIBtn size="sm" variant="secondary" onClick={handleSync} disabled={syncing}>
          {syncing ? 'Refreshing…' : 'Refresh GBP locations'}
        </GBIBtn>
      </div>
      {result && (
        result.error ? (
          <div style={{ marginTop: 10, padding: '8px 12px', fontSize: 12, background: 'rgba(245,160,32,0.08)', border: `1px solid ${T.amber}`, borderRadius: 6, color: T.amber }}>
            {result.error.includes('403') || result.error.includes('PERMISSION_DENIED')
              ? <>Google hasn't approved our app's GBP API access yet. This is expected — it'll start working once access is granted. Details: {result.error}</>
              : result.error}
          </div>
        ) : (
          <div style={{ marginTop: 10, padding: '8px 12px', fontSize: 12, background: 'rgba(24,128,56,0.08)', border: `1px solid ${T.green}`, borderRadius: 6, color: T.green }}>
            Found {result.locationsFound}, {result.locationsUpserted} saved.
          </div>
        )
      )}
    </GBICard>
  );
}

const INTAKE_STATUS_COLOR: Record<string, string> = {
  imported: T.green,
  ready: T.cyan,
  draft: T.fgMuted,
  archived: T.magenta,
};

function LocationRow({ location: l }: { location: GbpLocationOverview }) {
  const name = l.displayName ?? l.placeId ?? '(unnamed location)';

  return (
    <GBICard>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 15, fontWeight: 700, color: T.fgPrimary }}>{name}</div>
          <div style={{ fontSize: 12, color: T.fgSecondary, marginTop: 2 }}>{l.address ?? '—'}</div>
          {l.primaryCategory && (
            <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 2 }}>{l.primaryCategory}</div>
          )}
          <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 6 }}>via {l.grantedVia}</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          {l.client ? (
            <Link to={`/admin/clients/${l.client.id}`} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
              <GBIBadge color={T.green}>Account ✓</GBIBadge>
              <span style={{ fontSize: 12, color: T.fgSecondary }}>{l.client.name}</span>
            </Link>
          ) : (
            <GBIBadge color={T.amber}>No account yet</GBIBadge>
          )}

          {l.intake ? (
            <GBIBadge color={INTAKE_STATUS_COLOR[l.intake.status] ?? T.fgMuted}>{l.intake.status}</GBIBadge>
          ) : (
            <Link to="/admin/intake" style={{ textDecoration: 'none' }}>
              <GBIBadge color={T.amber}>No intake</GBIBadge>
            </Link>
          )}
        </div>
      </div>
    </GBICard>
  );
}

function Banner({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <div style={{
      marginBottom: 16, padding: '10px 14px',
      background: `${color}14`, border: `1px solid ${color}`, borderRadius: 8,
      fontSize: 13, color,
    }}>{children}</div>
  );
}
